import { User } from '@auth0/auth0-react';
import { USER_AUDIT_TRAIL } from '@verify/shared-utils';
import { ADMIN_ASSIGNABLE_ROLES, SUPER_ADMIN_ONLY_ROLES, UserRolesData } from 'types/myAccount';

const getUserField = (user: User, field: string, defaultValue: any): typeof defaultValue => {
  const key = `https://${import.meta.env.VITE_AUTH0_DOMAIN}_${field}`;
  if (!user && !user[key]) return defaultValue;
  return user[key];
};

export const getRoles = (user: User) => {
  return getUserField(user, 'roles', []);
};

export const getUserMetadata = (user: User) => {
  return getUserField(user, 'user_metadata', {});
};

export const getAppMetadata = (user?: User | undefined): Record<string, any> => {
  if (!user) return {};
  return getUserField(user, 'app_metadata', {});
};

export const getCreatedAt = (user: User) => {
  return getUserField(user, 'created_at', '');
};

export const getTenantId = (user: User) => {
  const appMetadata: any = getAppMetadata(user);
  return appMetadata?.tenantInfo?.tenantId || '';
};

export const getOrganizationId = (user: User) => {
  const appMetadata: any = getAppMetadata(user);
  return appMetadata?.org_id || '';
};

export const getValidationError = (user?: User): boolean => {
  if (!user) return false;
  return getUserField(user, 'validate_error', false);
};

const hasRole = (user: User, role: string): boolean => {
  const roles = getRoles(user);
  if (!roles) return false;
  return roles.includes(role);
};

export const isUser = (user: User): boolean => {
  return hasRole(user, 'user');
};

export const isAdmin = (user: User): boolean => {
  return hasRole(user, 'administrator');
};

export const isTenantManager = (user: User): boolean => {
  return hasRole(user, 'tenant_manager');
};

export const isSuperAdmin = (user: User): boolean => {
  return hasRole(user, 'superadmin');
};

export const isTenantAdmin = (user: User): boolean => {
  return hasRole(user, 'tenant_admin');
};

export const isCustomProfiler = (user: User): boolean => {
  return hasRole(user, 'custom_profiler');
};

export const isInspectionCloner = (user: User): boolean => {
  return hasRole(user, 'inspection_cloner');
};

export const hasAuditTrailRole = (user: User): boolean => {
  return hasRole(user, USER_AUDIT_TRAIL);
};

export const canAccessAuditTrail = (user: User): boolean => {
  return isSuperAdmin(user) || isAdmin(user) || isTenantAdmin(user) || hasAuditTrailRole(user);
};

/**
 * Narrows the roles returned by the API down to the ones a team member can be assigned.
 * Anything not explicitly allowed is excluded, so new backend roles stay hidden until listed.
 */
export const getAssignableRoles = (roles: UserRolesData[], superAdmin: boolean): UserRolesData[] => {
  return roles.filter(
    (role) => ADMIN_ASSIGNABLE_ROLES.includes(role.name) || (superAdmin && SUPER_ADMIN_ONLY_ROLES.includes(role.name)),
  );
};

/**
 * Parse comma-separated organization IDs from environment variable
 * @param envVar - Environment variable value (comma-separated Auth0 org IDs)
 * @returns Array of organization IDs
 * @example
 * parseAllowedOrganizations('org_yLDg1g9wS9WPeC2w,org_XmBt3k7jD2QaE9Vx')
 * // Returns: ['org_yLDg1g9wS9WPeC2w', 'org_XmBt3k7jD2QaE9Vx']
 */
export const parseAllowedOrganizations = (envVar: string): string[] => {
  if (!envVar) return [];
  return envVar
    .split(',')
    .map((org: string) => org.trim())
    .filter(Boolean);
};

export const getUserInfo = (user: User): any => {
  const roles = getRoles(user);
  const createdAt = getCreatedAt(user);
  return {
    ...user,
    roles,
    created_at: createdAt,
  };
};

export default {
  getRoles,
  getUserMetadata,
  getAppMetadata,
  getUserInfo,
};
